/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import dayjs from "dayjs";
import {
  DateRangePicker,
  DateRange,
  SegmentSelect,
  useLocalization,
} from "@lemmonhq/uikit";
import { getIcon } from "./TipsSummary";
import { ViewMode } from "./tips-types";

const VIEW_MODES: ViewMode[] = ["user", "table", "payment"];

/** Same keys the picker itself labels its presets with. */
const DATE_PRESET_KEYS: Record<string, string> = {
  today: "date_range_picker.options.today",
  yesterday: "date_range_picker.options.yesterday",
  this_week: "date_range_picker.options.this_week",
  last_week: "date_range_picker.options.last_week",
  this_month: "date_range_picker.options.this_month",
  last_month: "date_range_picker.options.last_month",
  this_quarter: "date_range_picker.options.this_quarter",
  last_quarter: "date_range_picker.options.last_quarter",
  this_year: "date_range_picker.options.this_year",
  last_year: "date_range_picker.options.last_year",
  last_12m: "date_range_picker.options.last_12_months",
};

export interface TipsFiltersProps {
  filters: {
    dateRange: DateRange;
    viewMode: ViewMode;
  };
  onFiltersChange: (filters: TipsFiltersProps["filters"]) => void;
  /** Called after Apply is tapped on mobile — lets PageHeader close the sheet. */
  onApply?: () => void;
}

/** The filters are behind a sheet on mobile, so the header line below the
 *  title is the only place the active period and grouping are visible there. */
export function buildTipsFilterSummary(
  filters: TipsFiltersProps["filters"],
  t: (key: string) => string,
): string {
  const { dateRange } = filters;
  const dateLabel =
    dateRange.type === "custom"
      ? (() => {
          const start = dayjs(dateRange.startDate).format("MMM D");
          const end = dayjs(dateRange.endDate).format("MMM D, 'YY");
          return start === dayjs(dateRange.endDate).format("MMM D")
            ? end
            : `${start} – ${end}`;
        })()
      : t(DATE_PRESET_KEYS[dateRange.type] ?? dateRange.type);

  return [dateLabel, t(`reports.tips_nav_words.${filters.viewMode}`)].join(
    " · ",
  );
}

/** Today grouped by user is the default the page opens with. */
export function countActiveTipsFilters(
  filters: TipsFiltersProps["filters"],
): number {
  return (
    (filters.dateRange.type !== "today" ? 1 : 0) +
    (filters.viewMode !== "user" ? 1 : 0)
  );
}

export const TipsFilters = defineComponent<TipsFiltersProps>({
  name: "TipsFilters",
  props: ["filters", "onFiltersChange", "onApply"],
  setup(props) {
    const { t } = useLocalization();

    const viewOptions = computed(() =>
      VIEW_MODES.map((mode) => ({
        value: mode,
        label: (
          <span class="flex items-center justify-center gap-2">
            <span class="material-symbols-outlined text-xl">
              {getIcon(mode)}
            </span>
            {t(`reports.tips_nav_words.${mode}`)}
          </span>
        ),
      })),
    );

    // Pending state — committed only when Apply is pressed (mobile).
    const pending = ref<TipsFiltersProps["filters"]>({ ...props.filters });

    const applySheet = () => {
      props.onFiltersChange({ ...pending.value });
      props.onApply?.();
    };

    // ── Desktop (inline in PageHeader) ───────────────────────────────────────
    const renderDesktop = () => (
      <>
        <DateRangePicker
          value={props.filters.dateRange}
          onChange={(dateRange) =>
            props.onFiltersChange({ ...props.filters, dateRange })
          }
        />
        <SegmentSelect
          options={viewOptions.value}
          value={props.filters.viewMode}
          onChange={(viewMode) =>
            viewMode &&
            props.onFiltersChange({
              ...props.filters,
              viewMode: viewMode as ViewMode,
            })
          }
        />
      </>
    );

    // ── Mobile sheet content (rendered inside PageHeader's BottomSheet) ──────
    const renderMobileSheet = () => (
      <div class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tips_nav_words.date_range")}
          </label>
          <DateRangePicker
            value={pending.value.dateRange}
            onChange={(dateRange) =>
              (pending.value = { ...pending.value, dateRange })
            }
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tips_nav_words.group_by")}
          </label>
          {/* One per row: three segments side by side do not fit a phone
              without the labels truncating to their icons. */}
          <div class="flex flex-col gap-2">
            {VIEW_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={pending.value.viewMode === mode}
                class={[
                  "btn justify-start gap-2 font-normal",
                  pending.value.viewMode === mode ? "btn-primary" : "btn-neutral",
                ].join(" ")}
                onClick={() =>
                  (pending.value = { ...pending.value, viewMode: mode })
                }
              >
                <span class="material-symbols-outlined text-xl">
                  {getIcon(mode)}
                </span>
                {t(`reports.tips_nav_words.${mode}`)}
              </button>
            ))}
          </div>
        </div>

        <button class="btn btn-primary btn-sm" onClick={applySheet}>
          <span class="material-symbols-outlined text-base">check</span>
          {t("reports.tips_nav_words.apply")}
        </button>
      </div>
    );

    return () => (
      <>
        {/* PageHeader shows this on desktop (md:block wrapper), hides on mobile */}
        <div class="hidden md:flex items-center gap-2 flex-wrap">
          {renderDesktop()}
        </div>
        {/* PageHeader shows this in the BottomSheet on mobile */}
        <div class="md:hidden">{renderMobileSheet()}</div>
      </>
    );
  },
});
